import { useCallback } from 'react'
import { getWordAtCell } from '../utils/gridUtils'

export default function useKeyboardNavigation({
  grid,
  userGrid,
  selectedCell,
  setSelectedCell,
  direction,
  setDirection,
  onLetterInput,
  onDelete,
  numberedCells,
  onToggleBlack,
  isEditorMode = false,
}) {
  const gridSize = grid?.length || 0

  const findNextCell = useCallback((row, col, dir, forward = true, skipBlack = true, skipFilled = false) => {
    if (!grid) return null

    const delta = forward ? 1 : -1

    if (dir === 'across') {
      let nextCol = col + delta
      while (nextCol >= 0 && nextCol < gridSize) {
        if (!skipBlack || !grid[row][nextCol].isBlack) {
          // Skip filled cells if requested and userGrid is available
          if (skipFilled && userGrid && userGrid[row][nextCol].letter) {
            nextCol += delta
            continue
          }
          return { row, col: nextCol }
        }
        nextCol += delta
      }
    } else {
      let nextRow = row + delta
      while (nextRow >= 0 && nextRow < gridSize) {
        if (!skipBlack || !grid[nextRow][col].isBlack) {
          // Skip filled cells if requested and userGrid is available
          if (skipFilled && userGrid && userGrid[nextRow][col].letter) {
            nextRow += delta
            continue
          }
          return { row: nextRow, col }
        }
        nextRow += delta
      }
    }

    return null
  }, [grid, gridSize, userGrid])

  const findNextWord = useCallback((forward = true, skipFilled = false, allowDirectionSwitch = true) => {
    if (!selectedCell || !numberedCells) return null

    const searchInDirection = (dir, startFromBeginning = false) => {
      const entries = dir === 'across'
        ? numberedCells.acrossEntries
        : numberedCells.downEntries

      if (entries.length === 0) return null

      // Find current word by checking which entry contains the selected cell
      let currentIdx = -1

      if (!startFromBeginning) {
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i]
          const wordInfo = getWordAtCell(grid, entry.row, entry.col, dir)
          if (wordInfo && wordInfo.cells) {
            const isInWord = wordInfo.cells.some(
              c => c.row === selectedCell.row && c.col === selectedCell.col
            )
            if (isInWord) {
              currentIdx = i
              break
            }
          }
        }
      }

      // If we couldn't find which word contains this cell, start from beginning
      if (currentIdx === -1) {
        currentIdx = forward ? -1 : 0
      }

      const startIdx = currentIdx
      let searchIdx = currentIdx
      let iterations = 0
      let wrapped = false

      do {
        searchIdx = forward
          ? (searchIdx + 1) % entries.length
          : (searchIdx - 1 + entries.length) % entries.length

        // Detect if we've wrapped around
        if (forward && searchIdx === 0 && iterations > 0) wrapped = true
        if (!forward && searchIdx === entries.length - 1 && iterations > 0) wrapped = true

        const nextEntry = entries[searchIdx]

        // If skipFilled is true and userGrid is available, find first empty cell in this word
        if (skipFilled && userGrid) {
          const wordInfo = getWordAtCell(grid, nextEntry.row, nextEntry.col, dir)
          if (wordInfo && wordInfo.cells) {
            for (const cell of wordInfo.cells) {
              if (!userGrid[cell.row][cell.col].letter) {
                return { row: cell.row, col: cell.col, direction: dir, wrapped }
              }
            }
            // All cells filled, continue to next word
            iterations++
            if (iterations >= entries.length) return { wrapped: true, allFilled: true }
            continue
          }
        }

        return { row: nextEntry.row, col: nextEntry.col, direction: dir, wrapped }
      } while (searchIdx !== startIdx && iterations < entries.length)

      // All words in this direction are filled
      return { wrapped: true, allFilled: true }
    }

    // First, search in current direction
    const result = searchInDirection(direction)

    // If we wrapped and direction switch is allowed, try the other direction
    if (result && result.wrapped && allowDirectionSwitch) {
      const otherDir = direction === 'across' ? 'down' : 'across'
      const otherResult = searchInDirection(otherDir, true)
      if (otherResult && !otherResult.allFilled) {
        return otherResult
      }
    }

    // Return current direction result (even if wrapped)
    if (result && !result.allFilled) {
      return result
    }

    // Fallback: all words filled, just return first entry
    const entries = direction === 'across'
      ? numberedCells.acrossEntries
      : numberedCells.downEntries
    if (entries.length > 0) {
      return { row: entries[0].row, col: entries[0].col, direction }
    }

    return null
  }, [selectedCell, direction, grid, numberedCells, userGrid])

  const handleKeyDown = useCallback((e) => {
    if (!grid || !selectedCell) return

    const { row, col } = selectedCell
    const skipBlack = !isEditorMode

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        if (direction === 'down') {
          const next = findNextCell(row, col, 'down', false, skipBlack)
          if (next) setSelectedCell(next)
        } else {
          // Switch direction or move up
          setDirection('down')
        }
        break

      case 'ArrowDown':
        e.preventDefault()
        if (direction === 'down') {
          const next = findNextCell(row, col, 'down', true, skipBlack)
          if (next) setSelectedCell(next)
        } else {
          setDirection('down')
        }
        break

      case 'ArrowLeft':
        e.preventDefault()
        if (direction === 'across') {
          const next = findNextCell(row, col, 'across', false, skipBlack)
          if (next) setSelectedCell(next)
        } else {
          setDirection('across')
        }
        break

      case 'ArrowRight':
        e.preventDefault()
        if (direction === 'across') {
          const next = findNextCell(row, col, 'across', true, skipBlack)
          if (next) setSelectedCell(next)
        } else {
          setDirection('across')
        }
        break

      case 'Tab':
        e.preventDefault()
        {
          const nextWord = findNextWord(!e.shiftKey, true)
          if (nextWord && nextWord.row !== undefined) {
            setSelectedCell({ row: nextWord.row, col: nextWord.col })
            if (nextWord.direction && nextWord.direction !== direction) {
              setDirection(nextWord.direction)
            }
          }
        }
        break

      case 'Enter':
        e.preventDefault()
        {
          const nextWord = findNextWord(!e.shiftKey, true)
          if (nextWord && nextWord.row !== undefined) {
            setSelectedCell({ row: nextWord.row, col: nextWord.col })
            if (nextWord.direction && nextWord.direction !== direction) {
              setDirection(nextWord.direction)
            }
          }
        }
        break

      case 'Backspace':
        e.preventDefault()
        if (grid[row][col].isBlack && isEditorMode) {
          // In editor mode, backspace on black cell just moves back
          const prev = findNextCell(row, col, direction, false, false)
          if (prev) setSelectedCell(prev)
        } else {
          // Check userGrid for current cell's letter (not solution grid)
          const currentHasLetter = userGrid && userGrid[row][col].letter
          if (currentHasLetter) {
            // Delete current cell's letter, then move back
            onDelete(row, col)
            const prev = findNextCell(row, col, direction, false, skipBlack)
            if (prev) {
              setSelectedCell(prev)
            }
          } else {
            // Cell is empty - just move back
            const prev = findNextCell(row, col, direction, false, skipBlack)
            if (prev) {
              setSelectedCell(prev)
            }
          }
        }
        break

      case ' ':
        e.preventDefault()
        // Toggle direction
        setDirection(d => d === 'across' ? 'down' : 'across')
        break

      case '.':
        // Toggle black square (editor mode only)
        if (onToggleBlack) {
          e.preventDefault()
          onToggleBlack(row, col)
        }
        break

      default:
        // Letter input
        if (/^[a-zA-Z]$/.test(e.key)) {
          e.preventDefault()
          if (!grid[row][col].isBlack) {
            onLetterInput(row, col, e.key.toUpperCase())

            // Get current word to find next cell within word boundaries
            const currentWord = getWordAtCell(grid, row, col, direction)
            const shouldSkipFilled = !isEditorMode

            if (currentWord && currentWord.cells) {
              // Find current position in word
              const currentPosInWord = currentWord.cells.findIndex(
                c => c.row === row && c.col === col
              )

              // Look for next cell in this word (optionally skipping filled)
              let nextCellInWord = null
              for (let i = currentPosInWord + 1; i < currentWord.cells.length; i++) {
                const cell = currentWord.cells[i]
                if (shouldSkipFilled && userGrid && userGrid[cell.row][cell.col].letter) {
                  continue // Skip filled cells
                }
                nextCellInWord = cell
                break
              }

              if (nextCellInWord) {
                setSelectedCell({ row: nextCellInWord.row, col: nextCellInWord.col })
              } else {
                // End of word - go to next word
                const nextWord = findNextWord(true, shouldSkipFilled)
                if (nextWord && nextWord.row !== undefined) {
                  setSelectedCell({ row: nextWord.row, col: nextWord.col })
                  if (nextWord.direction && nextWord.direction !== direction) {
                    setDirection(nextWord.direction)
                  }
                }
              }
            } else {
              // Fallback: no word found, use findNextWord
              const nextWord = findNextWord(true, shouldSkipFilled)
              if (nextWord && nextWord.row !== undefined) {
                setSelectedCell({ row: nextWord.row, col: nextWord.col })
                if (nextWord.direction && nextWord.direction !== direction) {
                  setDirection(nextWord.direction)
                }
              }
            }
          } else if (isEditorMode && onToggleBlack) {
            // In editor mode, typing a letter on a black cell clears it first
            onToggleBlack(row, col)
            onLetterInput(row, col, e.key.toUpperCase())
            const next = findNextCell(row, col, direction, true, false)
            if (next) {
              setSelectedCell(next)
            }
          }
        }
        break
    }
  }, [
    grid,
    selectedCell,
    direction,
    setDirection,
    setSelectedCell,
    findNextCell,
    findNextWord,
    onLetterInput,
    onDelete,
    onToggleBlack,
    isEditorMode
  ])

  return { handleKeyDown }
}
